const now = () => globalThis.performance?.now?.() ?? Date.now();

export class MetricsCollector {
    constructor({ frameWindowMs = 2000 } = {}) {
        this.frameWindowMs = frameWindowMs;
        this.reset();
    }

    reset() {
        this.cameraOpeningStartedAt = null;
        this.scannerStartedAt = null;
        this.cameraOpenTimeMs = null;
        this.firstReadTimeMs = null;
        this.lastReadAt = null;
        this.readIntervals = [];
        this.cameraFrameTimestamps = [];
        this.processingFrameTimestamps = [];
        this.processingDurations = [];
        this.cameraFrameCount = 0;
        this.processingFrameCount = 0;
        this.noCodeFrameCount = 0;
        this.readCount = 0;
        this.discardedCount = 0;
        this.errorCount = 0;
        this.recoveryCount = 0;
    }

    markCameraOpening(timestamp = now()) {
        this.cameraOpeningStartedAt = timestamp;
    }

    markCameraOpen(timestamp = now()) {
        if (this.cameraOpeningStartedAt !== null) {
            this.cameraOpenTimeMs = Math.max(
                0,
                timestamp - this.cameraOpeningStartedAt
            );
        }
    }

    markScannerStarted(timestamp = now()) {
        this.scannerStartedAt = timestamp;
    }

    recordCameraFrame(timestamp = now()) {
        this.cameraFrameCount++;
        this.cameraFrameTimestamps.push(timestamp);
        this.pruneFrames(this.cameraFrameTimestamps, timestamp);
    }

    recordProcessingFrame({
        timestamp = now(),
        durationMs = null,
        detected = false,
    } = {}) {
        this.processingFrameCount++;
        this.processingFrameTimestamps.push(timestamp);

        if (!detected) {
            this.noCodeFrameCount++;
        }

        if (Number.isFinite(durationMs)) {
            this.processingDurations.push(durationMs);

            if (this.processingDurations.length > 120) {
                this.processingDurations.shift();
            }
        }

        this.pruneFrames(this.processingFrameTimestamps, timestamp);
    }

    recordFrame(timestamp = now()) {
        this.recordProcessingFrame({ timestamp });
    }

    recordAcceptedRead(timestamp = now()) {
        if (
            this.firstReadTimeMs === null
            && this.scannerStartedAt !== null
        ) {
            this.firstReadTimeMs = Math.max(
                0,
                timestamp - this.scannerStartedAt
            );
        }

        if (this.lastReadAt !== null) {
            this.readIntervals.push(timestamp - this.lastReadAt);
        }

        this.lastReadAt = timestamp;
        this.readCount++;
    }

    recordDiscardedRead() {
        this.discardedCount++;
    }

    recordError() {
        this.errorCount++;
    }

    recordRecovery() {
        this.recoveryCount++;
    }

    pruneFrames(timestamps, timestamp = now()) {
        const threshold = timestamp - this.frameWindowMs;

        while (
            timestamps.length > 0
            && timestamps[0] < threshold
        ) {
            timestamps.shift();
        }
    }

    getFps(timestamps, timestamp = now()) {
        this.pruneFrames(timestamps, timestamp);

        if (timestamps.length < 2) {
            return 0;
        }

        const elapsed = (
            timestamps[timestamps.length - 1]
            - timestamps[0]
        ) / 1000;

        return elapsed > 0
            ? (timestamps.length - 1) / elapsed
            : 0;
    }

    getAverageProcessingTime() {
        if (this.processingDurations.length === 0) {
            return null;
        }

        return this.processingDurations.reduce(
            (total, duration) => total + duration,
            0
        ) / this.processingDurations.length;
    }

    getAverageReadInterval() {
        if (this.readIntervals.length === 0) {
            return null;
        }

        const total = this.readIntervals.reduce(
            (sum, interval) => sum + interval,
            0
        );

        return total / this.readIntervals.length;
    }

    snapshot(timestamp = now()) {
        const cameraFps = this.getFps(
            this.cameraFrameTimestamps,
            timestamp
        );
        const processingFps = this.getFps(
            this.processingFrameTimestamps,
            timestamp
        );

        return {
            cameraOpenTimeMs: this.cameraOpenTimeMs,
            firstReadTimeMs: this.firstReadTimeMs,
            averageReadIntervalMs: this.getAverageReadInterval(),
            cameraFps,
            processingFps,
            approximateFps: processingFps,
            averageProcessingTimeMs: this.getAverageProcessingTime(),
            cameraFrameCount: this.cameraFrameCount,
            processingFrameCount: this.processingFrameCount,
            frameCount: this.processingFrameCount,
            noCodeFrameCount: this.noCodeFrameCount,
            readCount: this.readCount,
            discardedCount: this.discardedCount,
            errorCount: this.errorCount,
            recoveryCount: this.recoveryCount,
        };
    }
}
